import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { api, describeError } from '../api.js';
import { useAppStore } from '../store.js';
import { Card, Notice, PageTitle } from '../ui.js';

// One request per token for the lifetime of the page module: React StrictMode runs effects
// twice in development, and a verification token is single-use.
const inflight = new Map<string, Promise<void>>();

function verify(token: string): Promise<void> {
  let pending = inflight.get(token);
  if (!pending) {
    pending = api('/api/v1/auth/verify-email', { method: 'POST', body: { token } }).then(
      () => undefined,
    );
    inflight.set(token, pending);
  }
  return pending;
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  // Read once, then dropped from the address bar and history: a single-use token has no
  // business lingering in a URL (ASVS 5.0 V14.2.1).
  const [token] = useState(() => params.get('token'));
  useEffect(() => {
    if (window.location.search) window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const refreshUser = useAppStore((s) => s.refreshUser);
  const [state, setState] = useState<{ kind: 'pending' | 'ok' | 'error'; message: string }>({
    kind: 'pending',
    message: 'Verifying…',
  });

  useEffect(() => {
    if (!token) {
      setState({ kind: 'error', message: 'This link is missing its token.' });
      return;
    }
    let cancelled = false;
    verify(token)
      .then(async () => {
        if (cancelled) return;
        setState({ kind: 'ok', message: 'Your email address is verified.' });
        await refreshUser();
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ kind: 'error', message: describeError(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [token, refreshUser]);

  return (
    <Card>
      <PageTitle>Email verification</PageTitle>
      <Notice
        kind={state.kind === 'ok' ? 'success' : state.kind === 'error' ? 'error' : 'info'}
        testId="verify-result"
      >
        {state.message}
      </Notice>
      <p>
        <Link to="/login">Continue to log in</Link>
      </p>
    </Card>
  );
}
